import "~/lib/sentry-client"
import * as Sentry from "@sentry/react"
import {
  Outlet,
  createRootRoute,
  HeadContent,
  Scripts,
} from "@tanstack/react-router"
import type { ReactNode } from "react"
import { Toaster } from "sonner"
import appCss from "~/styles/app.css?url"

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "CORE Snapshot" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com" },
    ],
  }),
  component: RootComponent,
  notFoundComponent: () => (
    <div className="flex-1 flex items-center justify-center px-4">
      <div className="text-center">
        <h2 className="text-3xl text-core-orange mb-2">Page Not Found</h2>
        <p className="text-white/60 mb-6">The page you are looking for does not exist.</p>
        <a
          href="/"
          className="inline-block rounded-full bg-core-orange px-8 py-3 text-white font-bold hover:opacity-90 transition-opacity"
        >
          Go Home
        </a>
      </div>
    </div>
  ),
})

function SiteHeader() {
  return (
    <header className="border-b border-white/10 print:hidden">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
        <a href="https://coreunites.com/">
          <img
            src="https://coreunites.com/wp-content/uploads/CORE_logo2018-white300px.png"
            width="160"
            height="41"
            alt="CORE Ministries"
            className="print:invert"
          />
        </a>
        <a
          href="https://coreunites.com"
          className="flex items-center gap-2 text-white/60 hover:text-core-orange transition-colors text-sm print:text-gray-500"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-5 h-5"
          >
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          <span className="hidden sm:inline">Return Home</span>
        </a>
      </div>
    </header>
  )
}

function SiteFooter() {
  return (
    <footer className="py-4 px-4 text-center text-sm text-white/40 border-t border-white/10 print:hidden">
      Copyright &copy; 2026 CORE Ministries |{" "}
      <a href="mailto:info@coreunites.com" className="underline hover:text-white/60 transition-colors">
        info@coreunites.com
      </a>{" "}
      |{" "}
      <a
        href="https://coreunites.com/privacy-policy/"
        className="underline hover:text-white/60 transition-colors"
      >
        privacy policy
      </a>
    </footer>
  )
}

function RootComponent() {
  return (
    <RootDocument>
      <div className="min-h-screen flex flex-col bg-core-black print:bg-white">
        <SiteHeader />
        <main className="flex-1 flex flex-col">
          <Sentry.ErrorBoundary
            fallback={
              <div className="flex-1 flex items-center justify-center px-4">
                <div className="text-center">
                  <h2 className="text-3xl text-core-orange mb-2">Something went wrong</h2>
                  <p className="text-white/60 mb-6">An unexpected error occurred.</p>
                  <a
                    href="/"
                    className="inline-block rounded-full bg-core-orange px-8 py-3 text-white font-bold hover:opacity-90 transition-opacity"
                  >
                    Go Home
                  </a>
                </div>
              </div>
            }
          >
            <Outlet />
          </Sentry.ErrorBoundary>
        </main>
        <SiteFooter />
      </div>
      <Toaster position="top-center" richColors />
    </RootDocument>
  )
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  )
}
